const SESSION_KEY = "signflow_token";

export function getStoredToken(): string | null {
  return localStorage.getItem(SESSION_KEY);
}

export function setStoredToken(token: string) {
  localStorage.setItem(SESSION_KEY, token);
}

export function clearStoredToken() {
  localStorage.removeItem(SESSION_KEY);
}

function authHeaders(): Record<string, string> {
  const token = getStoredToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try {
    headers["X-Timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {}
  return headers;
}

const FRIENDLY_MESSAGES: Record<string, string> = {
  "Authentication required": "Your session has expired. Please sign in again.",
  "Session expired. Please sign in again.": "Your session has expired. Please sign in again.",
  "Failed to fetch": "Unable to reach the server. Please check your internet connection and try again.",
  "Load failed": "Unable to reach the server. Please check your internet connection and try again.",
  "NetworkError": "A network error occurred. Please check your connection and try again.",
};

function friendlyMessage(raw: string, status?: number): string {
  for (const [key, friendly] of Object.entries(FRIENDLY_MESSAGES)) {
    if (raw.includes(key)) return friendly;
  }
  if (status && status >= 500) return "The server encountered a problem. Please try again in a moment.";
  if (status === 403) return "You don't have permission to perform this action.";
  if (status === 404) return "The requested item could not be found.";
  if (status === 409) return "This action conflicts with the current state. Please refresh and try again.";
  if (status === 429) return "Too many requests. Please wait a moment and try again.";
  return raw;
}

function isRetryable(status: number): boolean {
  return status === 0 || status === 408 || status === 429 || status === 502 || status === 503 || status === 504;
}

function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) {
    const msg = err.message.toLowerCase();
    return msg.includes("failed to fetch") || msg.includes("load failed") || msg.includes("networkerror");
  }
  return false;
}

async function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function isSafeToRetry(method?: string): boolean {
  const m = (method || "GET").toUpperCase();
  return m === "GET" || m === "HEAD" || m === "OPTIONS";
}

async function fetchWithRetry<T>(url: string, options?: RequestInit, maxRetries = 2): Promise<T> {
  const headers = { ...authHeaders(), ...(options?.headers as Record<string, string> || {}) };
  const canRetry = isSafeToRetry(options?.method) && maxRetries > 0;
  const effectiveRetries = canRetry ? maxRetries : 0;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= effectiveRetries; attempt++) {
    try {
      const res = await fetch(url, { ...options, headers });

      if (res.ok) return res.json();

      const body = await res.json().catch(() => ({ message: res.statusText }));
      const rawMsg = body.message || `Request failed: ${res.status}`;

      if (res.status === 401 && (rawMsg.includes("Session expired") || rawMsg.includes("Authentication required"))) {
        handleSessionExpiry();
      }

      if (canRetry && isRetryable(res.status) && attempt < effectiveRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 4000);
        await sleep(delay);
        continue;
      }

      const err = new Error(friendlyMessage(rawMsg, res.status));
      (err as any).status = res.status;
      (err as any).rawMessage = rawMsg;
      throw err;
    } catch (err: any) {
      if (err.status) throw err;

      if (canRetry && isNetworkError(err) && attempt < effectiveRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 4000);
        await sleep(delay);
        lastError = err;
        continue;
      }

      if (isNetworkError(err)) {
        throw new Error(friendlyMessage("Failed to fetch"));
      }

      throw err;
    }
  }

  throw lastError || new Error("Request failed after retries");
}

let sessionExpiryHandled = false;
function handleSessionExpiry() {
  if (sessionExpiryHandled) return;
  sessionExpiryHandled = true;
  clearStoredToken();
  setTimeout(() => {
    sessionExpiryHandled = false;
    if (!window.location.pathname.startsWith("/auth") &&
        !window.location.pathname.startsWith("/signing/") &&
        !window.location.pathname.startsWith("/dealform/") &&
        !window.location.pathname.startsWith("/webform/")) {
      window.location.href = "/auth";
    }
  }, 100);
}

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  return fetchWithRetry<T>(url, options);
}

async function uploadFile(file: File): Promise<any> {
  const formData = new FormData();
  formData.append("file", file);
  const token = getStoredToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try { headers["X-Timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch {}

  try {
    const res = await fetch("/api/upload", { method: "POST", body: formData, headers });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ message: res.statusText }));
      const rawMsg = body.message || `Upload failed: ${res.status}`;
      if (res.status === 401) handleSessionExpiry();
      throw new Error(friendlyMessage(rawMsg, res.status));
    }
    return res.json();
  } catch (err: any) {
    if (err.status) throw err;
    if (isNetworkError(err)) throw new Error(friendlyMessage("Failed to fetch"));
    throw err;
  }
}

async function downloadBlob(url: string, filename: string): Promise<void> {
  const token = getStoredToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try { headers["X-Timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch {}

  for (let attempt = 0; attempt <= 1; attempt++) {
    try {
      const res = await fetch(url, { headers });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ message: res.statusText }));
        const rawMsg = body.message || `Download failed: ${res.status}`;
        if (res.status === 401) handleSessionExpiry();
        if (isRetryable(res.status) && attempt < 1) {
          await sleep(2000);
          continue;
        }
        throw new Error(friendlyMessage(rawMsg, res.status));
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      const blobUrl = URL.createObjectURL(blob);
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
      return;
    } catch (err: any) {
      if (err.status) throw err;
      if (isNetworkError(err) && attempt < 1) {
        await sleep(2000);
        continue;
      }
      if (isNetworkError(err)) throw new Error(friendlyMessage("Failed to fetch"));
      throw err;
    }
  }
}

export async function apiFetch(url: string, options?: { method?: string; body?: any }) {
  return fetchWithRetry(url, {
    method: options?.method || "GET",
    body: options?.body ? JSON.stringify(options.body) : undefined,
  });
}

export const api = {
  get: <T = any>(url: string) => fetchJson<T>(url),
  post: <T = any>(url: string, data: any) => fetchJson<T>(url, { method: "POST", body: JSON.stringify(data) }),
  put: <T = any>(url: string, data: any) => fetchJson<T>(url, { method: "PUT", body: JSON.stringify(data) }),
  patch: <T = any>(url: string, data: any) => fetchJson<T>(url, { method: "PATCH", body: JSON.stringify(data) }),
  delete: <T = any>(url: string) => fetchJson<T>(url, { method: "DELETE" }),

  sendOtp: (email: string) => fetchJson<any>("/api/auth/send-otp", { method: "POST", body: JSON.stringify({ email }) }),
  resendOtp: (sessionId: string) => fetchJson<any>("/api/auth/resend-otp", { method: "POST", body: JSON.stringify({ sessionId }) }),
  verifyOtp: (sessionId: string, otp: string) => fetchJson<any>("/api/auth/verify-otp", { method: "POST", body: JSON.stringify({ sessionId, otp }) }),
  getSession: () => fetchJson<any>("/api/auth/session"),
  logout: () => fetchJson<any>("/api/auth/logout", { method: "POST" }),

  uploadPdf: uploadFile,
  getDocuments: () => fetchJson<any[]>("/api/documents"),
  getDocument: (id: string) => fetchJson<any>(`/api/documents/${id}`),
  createDocument: (data: any) => fetchJson<any>("/api/documents", { method: "POST", body: JSON.stringify(data) }),
  updateDocument: (id: string, data: any) => fetchJson<any>(`/api/documents/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteDocument: (id: string) => fetchJson<any>(`/api/documents/${id}`, { method: "DELETE" }),

  getSigners: (docId: string) => fetchJson<any[]>(`/api/documents/${docId}/signers`),
  addSigner: (docId: string, data: any) => fetchJson<any>(`/api/documents/${docId}/signers`, { method: "POST", body: JSON.stringify(data) }),
  updateSigner: (signerId: string, data: { fullName?: string; email?: string }) => fetchJson<any>(`/api/signers/${signerId}`, { method: "PATCH", body: JSON.stringify(data) }),
  signDocument: (signerId: string, data?: { signatureImage?: string | null; signatureAuthCode?: string; fieldValues?: Record<string, string> }) => fetchJson<any>(`/api/signers/${signerId}/sign`, { method: "PATCH", body: JSON.stringify(data || {}) }),
  removeSigner: (signerId: string) => fetchJson<any>(`/api/signers/${signerId}`, { method: "DELETE" }),
  replacePdf: async (docId: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const token = getStoredToken();
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    try { headers["X-Timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch {}
    const res = await fetch(`/api/documents/${docId}/replace-pdf`, { method: "POST", body: formData, headers });
    if (!res.ok) { const body = await res.json().catch(() => ({ message: res.statusText })); throw new Error(body.message || `Upload failed: ${res.status}`); }
    return res.json();
  },

  getAuditTrail: (docId: string) => fetchJson<any[]>(`/api/documents/${docId}/audit`),
  logAuditEvent: (docId: string, data: any) => fetchJson<any>(`/api/documents/${docId}/audit`, { method: "POST", body: JSON.stringify(data) }),

  getConsentRecords: (docId: string) => fetchJson<any[]>(`/api/documents/${docId}/consent`),
  recordConsent: (data: any) => fetchJson<any>("/api/consent", { method: "POST", body: JSON.stringify(data) }),

  getStats: () => fetchJson<any>("/api/stats"),

  getDocumentVersions: (docId: string) => fetchJson<any[]>(`/api/documents/${docId}/versions`),
  createDocumentVersion: (docId: string, data: any) => fetchJson<any>(`/api/documents/${docId}/versions`, { method: "POST", body: JSON.stringify(data) }),

  getCertificate: (docId: string) => fetchJson<any>(`/api/documents/${docId}/certificate`),

  getTemplateCategories: () => fetchJson<any[]>("/api/template-categories"),
  createTemplateCategory: (name: string) => fetchJson<any>("/api/template-categories", { method: "POST", body: JSON.stringify({ name }) }),
  deleteTemplateCategory: (id: string) => fetchJson<any>(`/api/template-categories/${id}`, { method: "DELETE" }),

  getTemplates: () => fetchJson<any[]>("/api/templates"),
  getTemplate: (id: string) => fetchJson<any>(`/api/templates/${id}`),
  createTemplate: (data: any) => fetchJson<any>("/api/templates", { method: "POST", body: JSON.stringify(data) }),
  updateTemplate: (id: string, data: any) => fetchJson<any>(`/api/templates/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteTemplate: (id: string) => fetchJson<any>(`/api/templates/${id}`, { method: "DELETE" }),
  useTemplate: (id: string, data: any) => fetchJson<any>(`/api/templates/${id}/use`, { method: "POST", body: JSON.stringify(data) }),
  quickSignTemplate: (id: string, data: any) => fetchJson<any>(`/api/templates/${id}/quick-sign`, { method: "POST", body: JSON.stringify(data) }),
  bulkSendTemplate: (id: string, data: any) => fetchJson<any>(`/api/templates/${id}/bulk-send`, { method: "POST", body: JSON.stringify(data) }),

  getDealForms: () => fetchJson<any[]>("/api/dealforms"),
  getDealForm: (id: string) => fetchJson<any>(`/api/dealforms/${id}`),
  createDealForm: (data: any) => fetchJson<any>("/api/dealforms", { method: "POST", body: JSON.stringify(data) }),
  updateDealForm: (id: string, data: any) => fetchJson<any>(`/api/dealforms/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteDealForm: (id: string) => fetchJson<any>(`/api/dealforms/${id}`, { method: "DELETE" }),
  copyDealForm: (id: string) => fetchJson<any>(`/api/dealforms/${id}/copy`, { method: "POST" }),
  getDealFormPublic: (id: string) => fetchJson<any>(`/api/dealforms/${id}/public`),
  useDealForm: (id: string, data: any) => fetchJson<any>(`/api/dealforms/${id}/submit`, { method: "POST", body: JSON.stringify(data) }),

  getTemplateFields: (templateId: string) => fetchJson<any[]>(`/api/templates/${templateId}/fields`),
  saveTemplateFields: (templateId: string, fields: any[]) => fetchJson<any[]>(`/api/templates/${templateId}/fields`, { method: "PUT", body: JSON.stringify({ fields }) }),
  deleteTemplateField: (templateId: string, fieldId: string) => fetchJson<any>(`/api/templates/${templateId}/fields/${fieldId}`, { method: "DELETE" }),

  getDocumentFields: (docId: string) => fetchJson<any[]>(`/api/documents/${docId}/fields`),
  saveDocumentFields: (docId: string, fields: any[]) => fetchJson<any[]>(`/api/documents/${docId}/fields`, { method: "PUT", body: JSON.stringify({ fields }) }),
  createDocumentField: (docId: string, field: any) => fetchJson<any>(`/api/documents/${docId}/fields`, { method: "POST", body: JSON.stringify(field) }),
  deleteDocumentField: (docId: string, fieldId: string) => fetchJson<any>(`/api/documents/${docId}/fields/${fieldId}`, { method: "DELETE" }),
  updateDocumentField: (docId: string, fieldId: string, value: string) => fetchJson<any>(`/api/documents/${docId}/fields/${fieldId}`, { method: "PATCH", body: JSON.stringify({ value }) }),

  downloadPdf: (docId: string, title: string) => downloadBlob(`/api/documents/${docId}/download-pdf`, `Completed with E-Sign - ${title.replace(/[^a-zA-Z0-9_\- ]/g, "").replace(/\s+/g, "_")}.pdf`),

  emailDocument: (docId: string, toEmail: string, includeType: "pdf" | "certificate" | "both") => fetchJson<any>(`/api/documents/${docId}/email`, { method: "POST", body: JSON.stringify({ toEmail, includeType }) }),

  getDocumentAudit: (docId: string) => fetchJson<any[]>(`/api/documents/${docId}/audit`),

  sendCompletedDocument: (docId: string, payload: { recipients: string[]; cc?: string[]; attachments: { signedPdf: boolean; auditTrail: boolean; certificate: boolean }; subject?: string; message?: string }) => fetchJson<any>(`/api/documents/${docId}/send-completed`, { method: "POST", body: JSON.stringify(payload) }),

  sendInvites: (docId: string, data?: { customSubject?: string; customMessage?: string; reminderEnabled?: boolean; reminderIntervalHours?: number; sendAll?: boolean }) => fetchJson<any>(`/api/documents/${docId}/send-invites`, { method: "POST", body: JSON.stringify(data || {}) }),
  updateReminders: (docId: string, data: { reminderEnabled: boolean; reminderIntervalHours?: number }) => fetchJson<any>(`/api/documents/${docId}/reminders`, { method: "PATCH", body: JSON.stringify(data) }),

  getSigningByToken: (token: string) => fetchJson<any>(`/api/signing/${token}`),
  signByToken: (token: string, data: any) => fetchJson<any>(`/api/signing/${token}/sign`, { method: "PATCH", body: JSON.stringify(data) }),
  updateFieldByToken: (token: string, fieldId: string, value: string) => fetchJson<any>(`/api/signing/${token}/fields/${fieldId}`, { method: "PATCH", body: JSON.stringify({ value }) }),
  consentByToken: (token: string) => fetchJson<any>(`/api/signing/${token}/consent`, { method: "POST" }),

  getWebFormSections: () => fetchJson<any[]>("/api/webform-sections"),
  createWebFormSection: (data: any) => fetchJson<any>("/api/webform-sections", { method: "POST", body: JSON.stringify(data) }),
  updateWebFormSection: (id: string, data: any) => fetchJson<any>(`/api/webform-sections/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteWebFormSection: (id: string) => fetchJson<any>(`/api/webform-sections/${id}`, { method: "DELETE" }),

  getWebForms: () => fetchJson<any[]>("/api/webforms"),
  getWebForm: (id: string) => fetchJson<any>(`/api/webforms/${id}`),
  createWebForm: (data: any) => fetchJson<any>("/api/webforms", { method: "POST", body: JSON.stringify(data) }),
  updateWebForm: (id: string, data: any) => fetchJson<any>(`/api/webforms/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteWebForm: (id: string) => fetchJson<any>(`/api/webforms/${id}`, { method: "DELETE" }),
  getWebFormPublic: (id: string) => fetchJson<any>(`/api/webforms/${id}/public`),
  submitWebForm: (id: string, data: any) => fetchJson<any>(`/api/webforms/${id}/submit`, { method: "POST", body: JSON.stringify(data) }),
  getWebFormSubmissions: (id: string) => fetchJson<any[]>(`/api/webforms/${id}/submissions`),

  getNotifications: () => fetchJson<any[]>("/api/notifications"),
  getUnreadNotificationCount: () => fetchJson<{ count: number }>("/api/notifications/unread-count"),
  markNotificationRead: (id: string) => fetchJson<any>(`/api/notifications/${id}/read`, { method: "PATCH" }),
  markAllNotificationsRead: () => fetchJson<any>("/api/notifications/mark-all-read", { method: "POST" }),
};
